import React from 'react'
import moment from 'moment'
import { calendarStyles, combineStyles } from './styles'
import { dayOptions } from './options'
import Hexagon from '../../../Assets/Images/icons/newIcons/hexagon'
import { CircularProgress, Tooltip } from '@material-ui/core'

import { makeStyles } from '@material-ui/core/styles'

const useStyles = makeStyles(() => ({
  tooltip: {
    padding: 0,
    fontSize: 'inherit',
    backgroundColor: 'white',
  },
}))
// Helper to get all days in a week (Sunday-Saturday)
const getWeekDays = weekStart => {
  return Array(7)
    .fill(0)
    .map((_, i) => moment(weekStart).add(i, 'days'))
}

const getWeeks = range => {
  const { start, end } = range
  const weeks = []
  let current = start.clone()
  while (current.isSameOrBefore(end)) {
    weeks.push(getWeekDays(current))
    current.add(1, 'week')
  }
  return weeks
}

const MultiDateCalendar = ({
  visibleRange = {
    start: moment()
      .subtract(2, 'weeks')
      .startOf('week'),
    end: moment()
      .add(2, 'weeks')
      .endOf('week'),
  },
  selectedDates = [],
  allowedDates = [],
  allowedWeekNumbers = [],
  onDateToggle,
  onWeekToggle,
  highlightedDates = [],
  header = '',
  tooltipContent = null,
  loading = false,
}) => {
  const today = moment()
  const weeks = getWeeks(visibleRange)

  const isAllowed = date => allowedDates.includes(date.format('YYYY-MM-DD'))
  const isSelected = date => selectedDates.includes(date.format('YYYY-MM-DD'))
  const isHighlighted = date =>
    highlightedDates.includes(date.format('YYYY-MM-DD'))
  // Helper to get the week number of selected dates
  const getSelectedWeekNumber = () => {
    if (selectedDates.length === 0) return null
    const firstSelectedDate = moment(selectedDates[0])
    return firstSelectedDate.format('W')
  }

  // Helper to check if a week should be disabled
  const isWeekDisabled = week => {
    const weekNumber = week[0].format('W')

    // Check if this week is in the allowed weeks
    const isAllowedWeek = allowedWeekNumbers.includes(weekNumber)
    if (!isAllowedWeek) return true

    const selectedWeekNumber = getSelectedWeekNumber()
    if (!selectedWeekNumber) return false

    // If dates are selected from one week, disable the other allowed week
    return weekNumber !== selectedWeekNumber
  }

  const handleDateClick = date => {
    if (!isAllowed(date)) return
    onDateToggle(date)
  }

  const handleWeekClick = week => {
    const weekDisabled = isWeekDisabled(week)
    if (weekDisabled) return
    onWeekToggle(week)
  }

  const TooltipWrapper = ({ children, enabled }) => {
    if (enabled) {
      return (
        <Tooltip
          title={tooltipContent}
          classes={{ tooltip: useStyles().tooltip }}
          placement="bottom-start"
        >
          {children}
        </Tooltip>
      )
    }
    return children
  }

  return (
    <div style={calendarStyles.container}>
      <div style={calendarStyles.header}>{header}</div>
      <div style={calendarStyles.calendarGrid}>
        {/* Header Row */}
        <div style={calendarStyles.headerRow}>
          <div style={calendarStyles.weekNumberHeader}>
            {loading && <CircularProgress size={10} />}
          </div>
          {dayOptions.map((d, index) => (
            <div
              key={d}
              style={combineStyles(
                calendarStyles.dayHeader,
                index === 6 && { borderRight: 'none' },
              )}
            >
              {d}
            </div>
          ))}
        </div>

        {/* Week Rows */}
        {weeks.map((week, weekIndex) => {
          const weekKeys = week
            .filter(isAllowed)
            .map(d => d.format('YYYY-MM-DD'))
          const allSelected =
            weekKeys.length > 0 &&
            weekKeys.every(k => selectedDates.includes(k))
          const hasAllowedDates = weekKeys.length > 0
          const weekDisabled = isWeekDisabled(week)

          const weekCellStyles = combineStyles(
            calendarStyles.weekNumberCell,
            (!hasAllowedDates || weekDisabled) &&
              calendarStyles.weekNumberCellDisabled,
            allSelected && calendarStyles.weekNumberCellSelected,
          )

          return (
            <div
              key={weekIndex}
              style={combineStyles(
                calendarStyles.weekRow,
                weekIndex === weeks.length - 1 && calendarStyles.weekRowLast,
              )}
            >
              {/* Week number cell */}
              <div style={weekCellStyles} onClick={() => handleWeekClick(week)}>
                W{week[0].format('W')}
              </div>

              {/* Date cells */}
              {week.map((date, dateIndex) => {
                const allowed = isAllowed(date)
                const highlighted = isHighlighted(date)
                const selectedCell = isSelected(date)
                const isToday = date.isSame(today, 'day')

                const cellStyles = combineStyles(
                  calendarStyles.dateCell,
                  allowed && !weekDisabled
                    ? calendarStyles.dateCellAllowed
                    : calendarStyles.dateCellDisabled,
                  selectedCell && calendarStyles.dateCellSelected,
                  isToday && calendarStyles.dateCellToday,
                )

                return (
                  <TooltipWrapper enabled={highlighted}>
                    <div
                      style={combineStyles(calendarStyles.dateCell, {
                        margin: '2px 4px',
                      })}
                    >
                      {highlighted && (
                        <div style={calendarStyles.highlightedCell}>
                          <Hexagon />
                        </div>
                      )}
                      <div
                        key={dateIndex}
                        onClick={() => handleDateClick(date)}
                        style={cellStyles}
                      >
                        {date.date()}
                      </div>
                    </div>
                  </TooltipWrapper>
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default MultiDateCalendar
